// Zentrale Typdefinitionen der Regel-Engine von „Schatten über Noxbury“.
//
// Die Engine ist rein und deterministisch: gleicher Zustand + gleicher Befehl + gleicher
// Kontext (Zeit, Zufall, Akteur) ergibt immer dasselbe Ergebnis. Sie kennt weder Netzwerk
// noch Datenbank. Der Server ist die einzige Instanz, die Befehle an die Engine übergibt.

// ---------------------------------------------------------------------------
// Rollen, Lager, Status
// ---------------------------------------------------------------------------

export const ROLE_IDS = [
  'VILLAGER',
  'VAMPIRE',
  'SEER',
  'CURSED',
  'WITCH',
  'PRIEST',
  'VAMPIRE_HUNTRESS',
  'FOOL',
  'DEMON',
] as const;

/** Tatsächliche (serverseitige) Rolle. Der Trunkenbold ist keine Rolle, sondern eine Maske. */
export type RoleId = (typeof ROLE_IDS)[number];

/** Rolle, wie sie ein Spieler selbst sieht. „DRUNK“ bis zum Trunkenbold-Reveal. */
export type VisibleRoleId = RoleId | 'DRUNK';

export type Team = 'VILLAGE' | 'VAMPIRE' | 'SOLO';
export type SeerAlignment = 'GOOD' | 'EVIL';

export type LifeState = 'ALIVE' | 'DEAD';
/** Administrativer Zustand, unabhängig von Leben/Tod. */
export type Presence = 'ACTIVE' | 'PAUSED' | 'WAITING' | 'REMOVED';
/** Technischer Status laut Spezifikation (abgeleitet aus LifeState + Presence). */
export type PlayerStatus = 'ALIVE' | 'DEAD' | 'PAUSED' | 'WAITING' | 'REMOVED';

export type WinType = 'VILLAGE' | 'VAMPIRE' | 'LOVERS' | 'FOOL' | 'DEMON';
export type DeathCause = 'VAMPIRE' | 'POISON' | 'DAY_VOTE' | 'LINKED' | 'HUNTRESS' | 'ADMIN';

/** Was der Priesterschutz abwehren kann – Tode und die Besessenheit des Dämons. */
export type BlockableCause = DeathCause | 'POSSESSION';

// ---------------------------------------------------------------------------
// Phasen
// ---------------------------------------------------------------------------

export const NIGHT_STEP_IDS = [
  'DRUNK_REVEAL',
  'LOVERS_PHASE',
  'VAMPIRE_PHASE',
  'SEER_PHASE',
  'WITCH_PHASE',
  'PRIEST_PHASE',
  'DEMON_PHASE',
  'GHOST_PHASE',
] as const;
export type NightStepId = (typeof NIGHT_STEP_IDS)[number];

export type Phase =
  | 'LOBBY'
  | 'ROLE_SETUP'
  | 'MAYOR_ELECTION'
  | 'GAME_START'
  | 'NIGHT_START'
  | NightStepId
  | 'NIGHT_RESOLUTION'
  | 'MORNING_REVEAL'
  | 'DEATH_RESOLUTION'
  | 'DISCUSSION'
  | 'DAY_VOTE'
  | 'VOTE_REVEAL'
  | 'DAY_VOTE_RESOLUTION'
  | 'MAYOR_TIEBREAK'
  | 'GAME_END';

export const PHASE_IDS: readonly Phase[] = [
  'LOBBY',
  'ROLE_SETUP',
  'MAYOR_ELECTION',
  'GAME_START',
  'NIGHT_START',
  ...NIGHT_STEP_IDS,
  'NIGHT_RESOLUTION',
  'MORNING_REVEAL',
  'DEATH_RESOLUTION',
  'DISCUSSION',
  'DAY_VOTE',
  'VOTE_REVEAL',
  'DAY_VOTE_RESOLUTION',
  'MAYOR_TIEBREAK',
  'GAME_END',
];

// ---------------------------------------------------------------------------
// Spieler & Ressourcen
// ---------------------------------------------------------------------------

export interface PlayerState {
  id: string;
  /** Öffentliche, eindeutige Spielernummer (auch für Twitch-Commands). */
  number: number;
  name: string;
  life: LifeState;
  presence: Presence;
  /** Tatsächliche Rolle (Server-Wahrheit). Beim Trunkenbold die verdeckte Rolle. */
  role: RoleId | null;
  /** Rolle bei Rundenstart (für Statistik; der Narr wird später Dorfbewohner). */
  originalRole: RoleId | null;
  diedRound: number | null;
}

export interface RoleResources {
  healPotion?: boolean;
  poisonPotion?: boolean;
  lastProtectedId?: string | null;
  lastProtectedNight?: number | null;
}

// ---------------------------------------------------------------------------
// Rollenkonfiguration
// ---------------------------------------------------------------------------

export interface DrunkSetup {
  enabled: boolean;
  /** Verdeckte Rolle des Trunkenbolds: konkrete Rolle aus der Komposition oder zufällig. */
  coveredRole: RoleId | 'RANDOM';
}

/** Der Geist hinterlässt Nacht für Nacht einen Buchstaben, den der Spielleiter morgens freigibt. */
export interface GhostState {
  playerId: string;
  /** Bereits freigegebene Buchstaben, in der Reihenfolge des Schreibens. */
  letters: string[];
  /** Noch nicht freigegebener Buchstabe der laufenden Nacht. */
  pending: string | null;
  /** Nacht, in der er zuletzt geschrieben hat. */
  pendingNight: number | null;
  /** Rang der Todesursache – entscheidet, wer bei mehreren Toten in derselben Nacht Geist wird. */
  claimRank: number;
}

export interface RoleSetup {
  /** Genau eine Rolle je Spieler. Der Trunkenbold belegt KEINEN zusätzlichen Platz. */
  roles: RoleId[];
  drunk: DrunkSetup;
  /** Spielt der Geist mit? Er ist keine Rolle, sondern fällt der ersten toten Person zu. */
  ghost?: boolean;
  /** Spielt das Geliebtenpaar mit? Es belegt keine zusätzlichen Rollenplätze. */
  lovers?: boolean;
}

export interface ManualAssignment {
  /** Feste Zuordnung Spieler → Rolle. Nicht zugeordnete Spieler werden zufällig verteilt. */
  assignments?: Record<string, RoleId>;
  /** Fester Trunkenbold (muss eine geeignete Rolle besitzen). */
  drunkPlayerId?: string | null;
}

export interface DrunkState {
  playerId: string;
  revealed: boolean;
}

// ---------------------------------------------------------------------------
// Nacht, Tag, Bürgermeister
// ---------------------------------------------------------------------------

export interface NightState {
  number: number;
  steps: NightStepId[];
  /** -1 = Nacht begonnen, noch keine Rolle aufgerufen. */
  stepIndex: number;
}

export type DayVoteResolution =
  | { type: 'ELIMINATED'; playerId: string; byMayor: boolean }
  | { type: 'TIE_NO_MAYOR'; candidates: string[] }
  | { type: 'NO_VOTES' };

export interface DayVoteState {
  round: number;
  isFirst: boolean;
  revealed: boolean;
  tieCandidates: string[];
  resolution: DayVoteResolution | null;
}

export interface TwitchMayorVote {
  /** Eindeutige Twitch-User-ID (niemals nur der Anzeigename). */
  userId: string;
  displayName: string;
  playerId: string;
  at: string;
}

export interface MayorElectionState {
  status: 'IDLE' | 'OPEN' | 'CLOSED' | 'SKIPPED';
  votes: Record<string, TwitchMayorVote>;
  openedAt: string | null;
  closedAt: string | null;
}

export interface MayorTerm {
  playerId: string;
  round: number;
  via: 'ELECTION' | 'SUCCESSION' | 'ADMIN';
}

// ---------------------------------------------------------------------------
// Aktionen
// ---------------------------------------------------------------------------

export type ActionKind =
  | 'VAMPIRE_ATTACK'
  | 'SEER_INVESTIGATE'
  | 'WITCH_POTIONS'
  | 'PRIEST_PROTECT'
  | 'HUNTRESS_TAKE'
  | 'DAY_VOTE'
  | 'MAYOR_TIEBREAK'
  | 'MAYOR_SUCCESSOR'
  | 'DEMON_POSSESS'
  | 'GHOST_LETTER';

export type ActionStatus = 'DRAFT' | 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'CANCELLED';
export type ActionOrigin = 'PLAYER' | 'HOST' | 'ADMIN';

export interface ActionPayload {
  /** Nur der Geist: genau ein Buchstabe. */
  letter?: string;
  targetId?: string | null;
  heal?: boolean;
  poisonTargetId?: string | null;
}

export interface ActionHistoryEntry {
  at: string;
  status: ActionStatus;
  payload: ActionPayload;
  by: ActionOrigin;
  note?: string;
}

export interface ActionRecord {
  id: string;
  kind: ActionKind;
  actorId: string;
  round: number;
  /** Kontext: „N3“ (Nacht 3), „D2“ (Tagesvote 2), „T2“ (Tiebreak 2) oder Effekt-ID. */
  contextId: string;
  payload: ActionPayload;
  status: ActionStatus;
  submittedBy: ActionOrigin;
  createdAt: string;
  updatedAt: string;
  confirmedAt: string | null;
  history: ActionHistoryEntry[];
}

// ---------------------------------------------------------------------------
// Effekte & Tod
// ---------------------------------------------------------------------------

/** Konzeptionelle Effektarten (Spezifikation Abschnitt 120). */
export type EffectType =
  | 'KILL'
  | 'NIGHT_KILL'
  | 'PROTECT'
  | 'HEAL'
  | 'POISON'
  | 'INVESTIGATE'
  | 'LINKED_DEATH'
  | 'ON_DEATH_KILL'
  | 'TRANSFER_MAYOR'
  | 'REVEAL_ROLE'
  | 'POSSESS'
  | 'WIN_CONDITION';

/** Interaktive Effekte, die in der Death-/Effect-Queue auf eine Entscheidung warten. */
export type QueuedEffectType = 'TRANSFER_MAYOR' | 'ON_DEATH_KILL';

export interface QueuedEffect {
  id: string;
  type: QueuedEffectType;
  /** Spieler, dessen Tod den Effekt ausgelöst hat (und der die Entscheidung trifft). */
  playerId: string;
  round: number;
  createdAt: string;
}

export interface DeathRecord {
  playerId: string;
  round: number;
  causes: DeathCause[];
  timing: 'NIGHT' | 'DAY';
  at: string;
}

export interface NightDeathPreview {
  playerId: string;
  causes: DeathCause[];
}

export interface BlockedNightEffect {
  playerId: string;
  cause: BlockableCause;
  blockedBy: 'PROTECT' | 'HEAL';
}

export interface SeerResultRecord {
  night: number;
  seerId: string;
  targetId: string;
  result: SeerAlignment;
  at: string;
}

export interface GameResult {
  winners: WinType[];
  detected: WinType[];
  endedAt: string;
  round: number;
  note: string | null;
}

// ---------------------------------------------------------------------------
// Gesamtzustand
// ---------------------------------------------------------------------------

export interface GameState {
  schemaVersion: 1;
  lobbyCode: string;
  /** Zählt „Neue Runde“ innerhalb derselben Lobby hoch. */
  gameNumber: number;
  phase: Phase;
  paused: boolean;
  /** 0 vor Nacht 1; Runde n = Nacht n + Tag n. */
  round: number;
  players: PlayerState[];
  setup: RoleSetup | null;
  rolesAssigned: boolean;
  drunk: DrunkState | null;
  lovers: [string, string] | null;
  loversRevealed: boolean;
  /** Vampire, die einander in einer Vampirphase bereits erkannt haben. */
  vampiresMet: string[];
  mayorId: string | null;
  mayorHistory: MayorTerm[];
  mayorElection: MayorElectionState;
  resources: Record<string, RoleResources>;
  night: NightState | null;
  dayVote: DayVoteState | null;
  firstDayVoteDone: boolean;
  actions: ActionRecord[];
  pendingNightDeaths: NightDeathPreview[];
  blockedNightEffects: BlockedNightEffect[];
  lastMorningDeaths: string[] | null;
  deathResolutionContext: 'MORNING' | 'DAY' | null;
  effectQueue: QueuedEffect[];
  deaths: DeathRecord[];
  seerResults: SeerResultRecord[];
  /** Vom Dämon besessene Spieler. Sichtbar für Spielleiter, Dämon und Overlay. */
  possessed: string[];
  /** Der Geist: fällt der ersten toten Person zu, sofern er eingeschaltet ist. */
  ghost: GhostState | null;
  /** Ereignisbasierte Siege (Narr). */
  eventWins: WinType[];
  /** Signatur der zuletzt gemeldeten Siegbedingungen (verhindert Doppelmeldungen). */
  winSignature: string;
  result: GameResult | null;
  counters: { seq: number; id: number };
}

// ---------------------------------------------------------------------------
// Events, Akteure, Befehle
// ---------------------------------------------------------------------------

/** HOST: nur Spielleiter. OVERLAY: Spielleiter + OBS. PUBLIC: alle (auch Spieler). */
export type Audience = 'HOST' | 'OVERLAY' | 'PUBLIC';

export type SoundCue =
  | 'NIGHT'
  | 'ROLE_CALL'
  | 'MORNING'
  | 'VOTING'
  | 'VOTE_REVEAL'
  | 'DEATH'
  | 'MAYOR_ELECTION'
  | 'DRUNK'
  | 'VICTORY';

export interface GameEvent {
  seq: number;
  at: string;
  type: string;
  audience: Audience;
  data: Record<string, unknown>;
  cue: SoundCue | null;
  gameNumber: number;
  round: number;
  phase: Phase;
}

export type Actor =
  | { type: 'HOST' }
  | { type: 'PLAYER'; playerId: string }
  | { type: 'TWITCH' }
  | { type: 'SYSTEM' };

export interface EngineContext {
  /** ISO-Zeitstempel des Befehls. */
  now: string;
  /** Zufallsquelle in [0, 1). Serverseitig kryptografisch geseedet, in Tests deterministisch. */
  random: () => number;
}

export type AdminChange =
  | { kind: 'SET_ROLE'; playerId: string; role: RoleId }
  | { kind: 'SET_LIFE'; playerId: string; life: LifeState; applyEffects?: boolean }
  | { kind: 'SET_PRESENCE'; playerId: string; presence: Presence }
  | { kind: 'SET_POTIONS'; playerId: string; heal: boolean; poison: boolean }
  | { kind: 'SET_PRIEST_LAST'; playerId: string; targetId: string | null; night: number | null }
  | { kind: 'SET_MAYOR'; playerId: string | null }
  | { kind: 'SET_LOVERS'; lovers: [string, string] | null }
  | { kind: 'SET_DRUNK'; playerId: string | null; revealed: boolean }
  | { kind: 'OVERRIDE_ACTION'; actionId: string; payload?: ActionPayload; status?: ActionStatus }
  | { kind: 'SET_PHASE'; phase: Phase }
  | { kind: 'RENAME'; playerId: string; name: string }
  | { kind: 'CLEAR_EFFECT'; effectId: string };

export type Command =
  | { type: 'ADD_PLAYER'; playerId: string; name: string }
  | { type: 'RENAME_PLAYER'; playerId: string; name: string }
  | { type: 'REMOVE_PLAYER'; playerId: string; reason?: string }
  | { type: 'OPEN_ROLE_SETUP' }
  | { type: 'RETURN_TO_LOBBY' }
  | { type: 'CONFIGURE_ROLES'; setup: RoleSetup }
  | { type: 'ASSIGN_ROLES'; manual?: ManualAssignment }
  | { type: 'START_MAYOR_ELECTION' }
  | { type: 'CLOSE_MAYOR_ELECTION' }
  | { type: 'SKIP_MAYOR_ELECTION' }
  | { type: 'CONFIRM_MAYOR'; playerId: string; override?: boolean }
  | { type: 'TWITCH_MAYOR_VOTE'; userId: string; displayName: string; playerNumber: number }
  | { type: 'START_NIGHT' }
  | { type: 'NEXT_NIGHT_STEP'; force?: boolean }
  | { type: 'RESOLVE_NIGHT'; force?: boolean }
  | { type: 'START_MORNING' }
  | { type: 'START_DEATH_RESOLUTION' }
  | { type: 'START_DISCUSSION' }
  | { type: 'START_DAY_VOTE' }
  | { type: 'REVEAL_VOTES' }
  | { type: 'RESOLVE_DAY_VOTE' }
  | {
      type: 'SUBMIT_ACTION';
      kind: ActionKind;
      payload: ActionPayload;
      /** Nur der Spielleiter: für wen gehandelt wird. */
      actorId?: string;
      /** Nur der Spielleiter: Entscheidung sofort festschreiben (Standard: ja). */
      confirm?: boolean;
    }
  | { type: 'CONFIRM_ACTION'; actionId: string }
  | { type: 'REJECT_ACTION'; actionId: string; reason?: string }
  | { type: 'RESET_ACTION'; actionId: string }
  | { type: 'SET_PAUSED'; paused: boolean }
  | { type: 'END_GAME'; winners: WinType[]; note?: string }
  | { type: 'NEW_ROUND' }
  | { type: 'ABORT_ROUND'; reason?: string }
  | { type: 'ADMIN'; change: AdminChange; reason?: string };

export type CommandType = Command['type'];

export interface DispatchResult {
  state: GameState;
  events: GameEvent[];
}
